import * as Sentry from '@sentry/browser'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import {
  kBoxOAuthCompletePath,
  kClioOAuthCompletePath,
  kDropboxOAuthCompletePath,
  kGoogleDriveOAuthCompletePath,
  kOneDriveOAuthCompletePath,
  kSharePointOAuthCompletePath,
} from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kExternalStorageProvider, type ExternalStorageProvider } from '@/constants/constants-external-storage'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { ExternalStorageOAuthCompleteResponse, ExternalStorageOAuthCompleteResponseSchema } from '@/settings/schemas/external-storage-schema'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

const PROVIDER_OAUTH_COMPLETE_PATH: Record<ExternalStorageProvider, string> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: kGoogleDriveOAuthCompletePath,
  [kExternalStorageProvider.DROPBOX]: kDropboxOAuthCompletePath,
  [kExternalStorageProvider.BOX]: kBoxOAuthCompletePath,
  [kExternalStorageProvider.ONEDRIVE]: kOneDriveOAuthCompletePath,
  [kExternalStorageProvider.CLIO]: kClioOAuthCompletePath,
  [kExternalStorageProvider.SHAREPOINT]: kSharePointOAuthCompletePath,
}

function formatErrorDetail(data: unknown): string {
  const responseData = data as { detail?: unknown } | undefined
  const detail = responseData?.detail ?? data
  if (typeof detail === 'string') return detail
  if (detail == null) return ''
  return JSON.stringify(detail)
}

/**
 * Finishes a provider connection after the OAuth callback redirected back to Settings.
 * The API exchanges the code only when the signed-in user started the flow.
 */
export default async function postExternalStorageOAuthComplete(
  provider: ExternalStorageProvider,
  handoffId: string
): Promise<ExternalStorageOAuthCompleteResponse> {
  const sentryTransactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error(kErrorCodeAuthError)

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      method: 'POST',
      body: JSON.stringify({ handoff_id: handoffId }),
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${PROVIDER_OAUTH_COMPLETE_PATH[provider]}`
    const response = await fetch(apiUrl, requestOptions)
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            provider,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail || `Failed to complete external storage connection: ${response.status}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('External storage OAuth completion response JSON parse failed'), {
          extra: { ...getResponseDebugContext(response, apiUrl), provider, parseError: formatUnknownError(parseError) },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid external storage OAuth completion response JSON')
    }

    const parsed = ExternalStorageOAuthCompleteResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('External storage OAuth completion response schema validation failed'), {
          extra: { ...getResponseDebugContext(response, apiUrl), provider, responseKeys: getTopLevelKeys(data), issues: parsed.error.issues },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid external storage OAuth completion response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: { provider } })
      })
    }
    throw error
  }
}
